import {
  ArrowUturnLeftIcon,
  BanknotesIcon,
  LightBulbIcon,
  PencilSquareIcon,
  PlusIcon,
  ReceiptRefundIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { ReturnPayoutModal } from 'components/ReturnPayoutModal'
import { sumBy, TotalsRow } from 'components/TableTotals'
import { isActiveServicingStatus, loanStatusConstants } from 'config'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  getLoanPayables,
  getLoanPayees,
  getPayableSuggestions,
  getServicingNotes,
  undoPayoutReturn,
  voidLoanPayable,
} from 'services'
import { Button } from 'stories/components'
import { confirm, formatDate, removeComma, thousandSeperatorWithPrefix } from 'utils'

import { usePayoffGuard } from '../payoffGuard'
import { type EditablePayable, type Payee, type Suggestion, AddPayableModal } from './AddPayableModal'
import { belongsOnPartyPayments } from './belongsOnPartyPayments'
import { belongsToAnotherRecord } from './belongsToAnotherRecord'

interface Payable {
  id: number
  payeeType: string
  payeeRefId: number | null
  payeeName: string
  payeeAddress: string
  amount: number
  memo: string
  ledgerCategory: string
  /** Which trust bucket paying this posts to, and the label the server gave it. */
  ledgerBucket: string
  ledgerBucketLabel: string
  status: string
  referenceNumber: string
  paymentMethod: string
  paidDate: string
  /** Set with status 'returned': when the bank sent the money back, and what it said. */
  returnedDate: string
  returnedReason: string
  /** The payment raised to pay this again, when the return asked for one. */
  reissuedAsPayableId: number | null
  /** Set on that copy: the returned payment it is a second attempt at. */
  reissueOfPayableId: number | null
  /** 'TMO' marks a payment converted from the previous servicer rather than raised here. */
  sourceType: string
  createdBy: string
  createdAt: string
}

const STATUS_STYLE: Record<string, string> = {
  queued: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  voided: 'bg-gray-100 text-gray-500',
  returned: 'bg-orange-100 text-orange-700',
}

/**
 * Payments this loan owes to someone other than a participant distribution — a borrower refund, a
 * contractor draw, a vendor invoice. Raising one puts it in the servicing check queue, where it is
 * paid by check or ACH alongside participant distributions; paying it posts a Disbursement to the
 * loan's trust ledger.
 */
export default function PartyPayments() {
  // Warns before raising a payment against an active payoff.
  const confirmPayoff = usePayoffGuard()
  const servicingStatus = useSelector((state: any) => state.loanDetail?.servicingStatus)
  const [loading, setLoading] = useState(false)
  const [payables, setPayables] = useState<Payable[]>([])
  const [payees, setPayees] = useState<Payee[]>([])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [addOpen, setAddOpen] = useState(false)
  const [prefill, setPrefill] = useState<Partial<Suggestion> | null>(null)
  const [editing, setEditing] = useState<EditablePayable | null>(null)
  const [returning, setReturning] = useState<Payable | null>(null)
  // Which bucket this loan's construction money sits in, as the server resolves it, so a draw
  // raised here opens on the account it actually comes out of. See `initialLedgerBucket`.
  const [constructionBucket, setConstructionBucket] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const notes: any = await getServicingNotes().catch(() => null)
      const suspense = Number(removeComma(String(notes?.data?.suspenseBalance ?? ''))) || 0
      const [list, who, sugg]: any[] = await Promise.all([
        getLoanPayables(),
        getLoanPayees(),
        getPayableSuggestions(suspense),
      ])
      const partyPayments = (Array.isArray(list?.items) ? list.items : []).filter(
        (item: Payable) => belongsOnPartyPayments(item),
      )
      setPayables(partyPayments)
      setConstructionBucket(String(list?.constructionBucket || ''))
      setPayees(Array.isArray(who?.payees) ? who.payees : [])
      setSuggestions(Array.isArray(sugg?.suggestions) ? sugg.suggestions : [])
    } catch {
      toast('Could not load payments for this loan.', { type: 'error' })
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  /**
   * A loan whose servicing status the company counts as closed - Paid in Full out of the box - is
   * WARNED about, not blocked. Row 369 refused a hand-keyed payment outright and that stopped real
   * work: a closed file usually still owes something. So the notice below says what the loan is,
   * and the server asks for a confirmation on submit (`confirm` in the response, answered with
   * `confirmClosedLoan`). What the loan's own records justify - the suggestions below, Invoices,
   * the Trust Ledger - carries its own source and is never warned about.
   */
  const closedAs = useMemo(() => {
    const status = String(servicingStatus || '')
    if (!status || isActiveServicingStatus(status)) return ''
    return (loanStatusConstants.servicingStatus as Record<string, string>)[status] || status
  }, [servicingStatus])

  const queuedTotal = useMemo(
    () => payables.filter((p) => p.status === 'queued').reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [payables],
  )

  /**
   * A queued payment can still be corrected: nothing has left yet and nothing has posted to the
   * trust ledger. One whose figures are kept by another record - a loan payment's pay-others line,
   * a charge split, an approved voucher - is not editable here, because that record would overwrite
   * the edit the next time it was saved; the server refuses those with the same reason.
   */
  const onEdit = async (p: Payable) => {
    if (!(await confirmPayoff())) return
    setPrefill(null)
    setEditing({
      id: p.id,
      payeeType: p.payeeType,
      payeeRefId: p.payeeRefId ?? null,
      payeeName: p.payeeName,
      payeeAddress: p.payeeAddress || '',
      amount: Number(p.amount) || 0,
      memo: p.memo || '',
      ledgerCategory: p.ledgerCategory || '',
      ledgerBucket: p.ledgerBucket || '',
    })
    setAddOpen(true)
  }

  const onVoid = async (p: Payable) => {
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Void the {thousandSeperatorWithPrefix(p.amount)} payment to <span className="font-semibold">{p.payeeName}</span>?
        <p className="text-sm mt-2">It is removed from the check queue and won&apos;t be paid.</p>
      </div>
    )
    if (!(await confirm(message))) return
    const res: any = await voidLoanPayable(p.id)
    if (res?.success) {
      toast(res.message || 'Payment voided.', { type: 'success' })
      load()
    } else toast(res?.message || 'Could not void this payment.', { type: 'error' })
  }

  /**
   * Take back a return keyed in by mistake. The deposit that booked the money coming back is
   * removed and the payment stands as paid again; a reissue still waiting in the queue is voided
   * with it, because it was only raised to replace a payment that did not fail after all.
   */
  const onUndoReturn = async (p: Payable) => {
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Undo the return of {thousandSeperatorWithPrefix(p.amount)} to{' '}
        <span className="font-semibold">{p.payeeName}</span>?
        <p className="text-sm mt-2">
          The deposit that booked the money coming back is removed and this goes back to paid.
          {!!p.reissuedAsPayableId && ' Its reissue is voided if it has not been paid yet.'}
        </p>
      </div>
    )
    if (!(await confirm(message))) return
    const res: any = await undoPayoutReturn(p.id)
    if (res?.success) {
      toast(res.message || 'Return undone.', { type: 'success' })
      load()
    } else toast(res?.message || 'Could not undo this return.', { type: 'error' })
  }

  return (
    <div className="party-payments-container relative">
      <LayoutLoading show={loading} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BanknotesIcon className="w-6 h-6 text-shade-blue" />
            Party Payments
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Money this loan owes a borrower, contractor or vendor. Raising a payment puts it in the servicing check
            queue; paying it there posts a disbursement to the trust bucket the payment names, or to none of them
            if it was raised not to post.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Awaiting payment</div>
          <div className="text-lg font-bold">{thousandSeperatorWithPrefix(queuedTotal)}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Button
          onClick={async () => {
            if (!(await confirmPayoff())) return
            setPrefill(null)
            setEditing(null)
            setAddOpen(true)
          }}
          className="!py-1.5"
        >
          <span className="flex items-center gap-1">
            <PlusIcon className="w-4 h-4" /> Pay a Party
          </span>
        </Button>
        <Button color="gray" onClick={load} className="!py-1.5">
          Refresh
        </Button>
        {!!closedAs && (
          <p className="text-sm text-amber-700">
            This loan is <span className="font-semibold">{closedAs}</span>. A payment keyed in here will post to a
            closed file, so you will be asked to confirm it. What the loan still owes can also be raised below or
            from Invoices and the Trust Ledger.
          </p>
        )}
      </div>

      {/* Things the loan's own records already justify — one click rather than rekeying. */}
      {suggestions.length > 0 && (
        <div className="mb-5 rounded border border-shade-blue/30 bg-shade-blue/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-shade-blue mb-2 flex items-center gap-1">
            <LightBulbIcon className="w-4 h-4" /> Suggested from this loan
          </p>
          <div className="flex flex-col gap-1.5">
            {suggestions.map((s) => (
              <div key={`${s.sourceType}-${s.sourceId}`} className="flex items-center justify-between gap-3 text-sm">
                <span>{s.label}</span>
                <button
                  type="button"
                  className="text-shade-blue hover:underline whitespace-nowrap"
                  onClick={async () => {
                    if (!(await confirmPayoff())) return
                    setEditing(null)
                    setPrefill(s)
                    setAddOpen(true)
                  }}
                >
                  Raise this payment
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900">
          <thead className="text-xs text-gray-700 uppercase bg-gray-100">
            <tr>
              <th className="px-4 py-3">Payee</th>
              <th className="px-2 py-3">Type</th>
              <th className="px-2 py-3">Reason</th>
              <th className="px-2 py-3">Trust Ledger Category</th>
              <th className="px-2 py-3">Posts To</th>
              <th className="px-2 py-3 text-right">Amount</th>
              <th className="px-2 py-3">Status</th>
              <th className="px-2 py-3">Check / Ref #</th>
              <th className="px-2 py-3">Raised</th>
              <th className="px-2 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {payables.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-5 text-gray-500">
                  No party payments raised on this loan yet. Vendor payments are paid from the
                  check queue, and converted history is on the payee&apos;s file under Accounting.
                </td>
              </tr>
            )}
            {payables.map((p, index) => (
              <tr key={p.id} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                <td className="px-4 py-3 font-medium">{p.payeeName}</td>
                <td className="px-2 py-3">{p.payeeType}</td>
                <td className="px-2 py-3 text-gray-600">{p.memo || ''}</td>
                <td className="px-2 py-3 text-gray-600">
                  {p.ledgerCategory || <span className="text-gray-400">&mdash;</span>}
                </td>
                <td className="px-2 py-3 text-gray-600">
                  {p.ledgerBucket === 'none' ? (
                    <span className="text-gray-400 italic">Not posted</span>
                  ) : (
                    p.ledgerBucketLabel || <span className="text-gray-400">&mdash;</span>
                  )}
                </td>
                <td className="px-2 py-3 text-right font-semibold">{thousandSeperatorWithPrefix(p.amount)}</td>
                <td className="px-2 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[p.status] || 'bg-gray-100'}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-2 py-3">
                  {p.referenceNumber ? (
                    <span>
                      <span className={p.status === 'returned' ? 'text-orange-700' : ''}>{p.referenceNumber}</span>
                      <span className="text-gray-400 text-[11px] ml-1 uppercase">{p.paymentMethod}</span>
                      {/* The reference is kept on a returned payment on purpose - it is what the
                          bank statement shows the money going out and coming back under. */}
                      {p.status === 'returned' && (
                        <span className="block text-[11px] text-orange-700">
                          returned {p.returnedDate || ''}
                          {p.returnedReason ? ` - ${p.returnedReason}` : ''}
                        </span>
                      )}
                    </span>
                  ) : p.status === 'queued' ? (
                    <span className="text-xs text-gray-500">
                      in check queue
                      {!!p.reissueOfPayableId && <span className="block text-[11px] text-orange-700">reissue</span>}
                    </span>
                  ) : (
                    ''
                  )}
                </td>
                <td className="px-2 py-3 text-gray-600">{p.createdAt ? formatDate(p.createdAt) : ''}</td>
                <td className="px-2 py-3">
                  {p.status === 'queued' && (
                    <div className="flex items-center gap-3">
                      {belongsToAnotherRecord(p.sourceType) ? (
                        <span className="text-xs text-gray-400">{belongsToAnotherRecord(p.sourceType)}</span>
                      ) : (
                        <span
                          className="text-shade-blue cursor-pointer inline-flex items-center gap-1"
                          onClick={() => onEdit(p)}
                        >
                          <PencilSquareIcon className="w-4 h-4" /> Edit
                        </span>
                      )}
                      <span
                        className="text-red-600 cursor-pointer inline-flex items-center gap-1"
                        onClick={() => onVoid(p)}
                      >
                        <TrashIcon className="w-4 h-4" /> Void
                      </span>
                    </div>
                  )}
                  {/* A paid payment is the one that can come back. Voiding it is refused by then:
                      the money left, so the way back is to record the return, not to erase it. */}
                  {p.status === 'paid' && (
                    <span
                      className="text-orange-700 cursor-pointer inline-flex items-center gap-1"
                      onClick={() => setReturning(p)}
                    >
                      <ReceiptRefundIcon className="w-4 h-4" /> Returned
                    </span>
                  )}
                  {p.status === 'returned' && (
                    <span
                      className="text-shade-blue cursor-pointer inline-flex items-center gap-1 text-xs"
                      onClick={() => onUndoReturn(p)}
                    >
                      <ArrowUturnLeftIcon className="w-4 h-4" /> Undo return
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {payables.length > 0 && (
            <TotalsRow
              tdClass="px-2 py-3"
              cells={[
                { value: 'Totals', className: 'px-4 py-3' },
                '',
                '',
                '',
                sumBy(payables, 'amount'),
                '',
                '',
                '',
                '',
              ]}
            />
          )}
        </table>
      </div>

      {!!returning && (
        <ReturnPayoutModal
          payout={returning}
          onClose={() => setReturning(null)}
          onDone={() => {
            setReturning(null)
            load()
          }}
        />
      )}

      {addOpen && (
        <AddPayableModal
          payees={payees}
          prefill={prefill}
          editing={editing}
          constructionBucket={constructionBucket}
          onClose={() => {
            setAddOpen(false)
            setEditing(null)
          }}
          onDone={() => {
            setAddOpen(false)
            setEditing(null)
            load()
          }}
        />
      )}
    </div>
  )
}
